import { useEffect, useState } from "react";

import { notFound } from "./NotFoundPage.jsx";
import { head } from "../components/common/PageHeader.jsx";
import { btn, area, errorSlot } from "../components/forms/FormControls.jsx";
import { detailRows } from "../components/common/DetailRows.jsx";
import { money, esc } from "../utils/format.js";
import { nodes } from "../components/common/reactHelpers.jsx";
import { badge } from "../components/common/StatusBadge.jsx";
import { tableViews } from "../components/tables/RecordTable.jsx";

const CUSTOMERS_API = "https://api.praachinorganics.com/api/Customers";

function getStatus(status) {
  const statuses = {
    1: "Active",
    2: "Inactive",
    3: "Blocked",
  };

  return statuses[status] || status || "";
}

function formatDate(date) {
  if (!date) return "";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleDateString();
}

export function customerDetail(cid, customerData) {
  if (!cid || !customerData) return notFound();

  const customerId = customerData.customerProfileId;

  const name = customerData.customerName || "—";
  const identifier = customerData.customerIdentifier || "—";
  const email = customerData.email || "—";
  const phone = customerData.phone || "— Not available";
  const city = customerData.city || "No city on file";
  const address = customerData.fullAddress || "—";
  const registration =
    formatDate(customerData.registrationDate) || "— Not recorded";
  const status = getStatus(customerData.status) || "—";
  const orderCount = customerData.orderCount ?? 0;
  const orderValue = customerData.nonCancelledOrderValue ?? 0;
  // const note = customerData.supportNotes || "";

  const orders = customerData.orderHistory || [];

  return (
    <>
      {head(
        "CUSTOMER DETAIL",
        name,
        city,
        btn(
          "Edit customer",
          `data-nav="customer-edit" data-id="${customerId}"`,
        ),
      )}

      <div className={"grid-two"}>
        <section className={"card"}>
          <h2>{"Customer record"}</h2>

          {detailRows([
            ["Name", name],
            ["Customer identifier", identifier],
            ["Email", email],
            ["Phone", phone],
            ["City", city === "No city on file" ? "—" : city],
            ["Address", address],
            ["Registration date", registration],
            ["Account status", status],
            ["Orders", orderCount],
            ["Non-cancelled order value", money(orderValue)],
          ])}

          {/* <form id={"customer-form"}>
            {area("Support notes", "note", note)}

            <button className={"btn"} type={"submit"}>
              {"Save changes"}
            </button>

            {errorSlot()}
          </form> */}
        </section>

        <section className={"card"}>
          <h2>
            {"Linked order history ("}
            {orderCount}
            {")"}
          </h2>

          {nodes(
            orders.map((o) => (
              <button
                className={"attention-row"}
                data-nav={"order"}
                data-id={o.orderId}
                key={`${o.orderId}-${o.productId}`}
              >
                {`Product ID: ${o.productId || "—"}`}
                {" · "}
                {esc(formatDate(o.date) || "—")}
                {" · "}
                {money(o.grandTotal)}
              </button>
            )),
          ) || <p className={"empty"}>{"No linked orders."}</p>}
        </section>
      </div>
    </>
  );
}

export default function CustomerDetailsPage({ recordId } = {}) {
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (recordId == null || recordId === "") {
      setCustomerData(null);
      setError("Customer ID is missing.");
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    async function loadCustomer() {
      setLoading(true);
      setError("");
      setCustomerData(null);

      try {
        const response = await fetch(
          `${CUSTOMERS_API}/${encodeURIComponent(recordId)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        if (response.status === 404) {
          setError("Customer not found.");
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load customer (${response.status}).`);
        }

        const data = await response.json();
        setCustomerData(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading customer:", err);
          setError(err.message || "Unable to load customer.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadCustomer();

    return () => controller.abort();
  }, [recordId]);

  if (loading) {
    return <div className={"card"}>{"Loading customer details..."}</div>;
  }

  if (error) {
    if (error === "Customer not found.") {
      return notFound();
    }

    return <div className={"card"}>{esc(error)}</div>;
  }

  return tableViews(customerDetail(recordId, customerData));
}
